/**
 * Connector auth:health / get_auth_health — sign-in family burned (reauth_required).
 * Distinct from service-session-dead (remote access banner while web JWT still valid).
 */

import { interceptInvoluntaryLogout } from './authDeclawShadow';
import {
  hasReachedUsableAuthedState,
  latchTerminalAuthFamily,
} from './authTerminalLatch';

export type AuthHealthReason = 'refresh_token_already_used' | 'family_revoked';

export type AuthHealthPayload =
  | { status: 'healthy' }
  | { status: 'degraded' }
  | {
      status: 'reauth_required';
      reason?: AuthHealthReason;
      since_unix?: number;
    };

let _reauthRequired = false;
let _lastPayload: Extract<AuthHealthPayload, { status: 'reauth_required' }> | null = null;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const fn of listeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function isAuthReauthRequired(): boolean {
  return _reauthRequired;
}

export function getAuthReauthPayload(): typeof _lastPayload {
  return _lastPayload;
}

export function subscribeAuthHealth(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function markAuthHealthy(): void {
  if (!_reauthRequired && !_lastPayload) return;
  _reauthRequired = false;
  _lastPayload = null;
  notifyListeners();
}

function markAuthReauthRequired(
  payload: Extract<AuthHealthPayload, { status: 'reauth_required' }>,
): void {
  if (payload.reason === 'family_revoked' && !hasReachedUsableAuthedState()) {
    latchTerminalAuthFamily('family_revoked');
  }
  if (interceptInvoluntaryLogout('reauth_required_panel', payload.reason ?? 'reauth_required', {
    payload,
  })) {
    return;
  }
  _reauthRequired = true;
  _lastPayload = payload;
  notifyListeners();
}

/** Parse connector auth:health / get_auth_health payloads. */
export function applyAuthHealthPayload(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return;
  const status = String((raw as Record<string, unknown>).status ?? '').trim();
  if (status === 'healthy') {
    markAuthHealthy();
    return;
  }
  if (status === 'degraded') {
    return;
  }
  if (status === 'reauth_required') {
    const obj = raw as Record<string, unknown>;
    const reasonRaw = String(obj.reason ?? '').trim();
    const reason = (
      reasonRaw === 'refresh_token_already_used' || reasonRaw === 'family_revoked'
    )
      ? reasonRaw
      : undefined;
    const sinceUnix = Number(obj.since_unix);
    markAuthReauthRequired({
      status: 'reauth_required',
      ...(reason ? { reason } : {}),
      ...(Number.isFinite(sinceUnix) && sinceUnix > 0 ? { since_unix: sinceUnix } : {}),
    });
  }
}

/** True when an error string carries the connector auth_reauth_required code. */
export function isAuthReauthRequiredError(errMsg: string | null | undefined): boolean {
  if (!errMsg) return false;
  const lower = errMsg.toLowerCase();
  return lower.includes('auth_reauth_required');
}

/** Suppress play-failure toasts for auth_reauth_required while the blocking panel is active. */
export function shouldSuppressAuthReauthPlayToast(errMsg?: string | null): boolean {
  return isAuthReauthRequired() && isAuthReauthRequiredError(errMsg);
}

/** Reset module state (tests). */
export function resetAuthHealthStateForTests(): void {
  _reauthRequired = false;
  _lastPayload = null;
  notifyListeners();
}
